import React, { useState, useEffect, useRef } from "react";

import { useStripe, useElements } from "@stripe/react-stripe-js";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

import { getAuthHeaders } from "../utils/apiClient";

import Loader from "./Loader";

import { BRAND_FULL } from "../constants/brand";



const STRIPE_ELEMENT_STYLE = {

  base: {

    color: "#eef2f7",

    fontSize: "16px",

    fontFamily: '"JetBrains Mono", ui-monospace, monospace',

  },

};



const MOUNT_IDS = ["card-number", "card-expiry", "card-cvc"];



function CardDetailsElements({ cardId, frozen = false }) {

  const [isLoading, setIsLoading] = useState(true);

  const [revealed, setRevealed] = useState(false);

  const [error, setError] = useState("");

  const stripe = useStripe();

  const elements = useElements();

  const mountedCardIdRef = useRef(null);



  useEffect(() => {

    if (!stripe || !elements || !cardId) {

      return;

    }



    if (mountedCardIdRef.current === cardId) {

      return;

    }



    let cancelled = false;

    const mountedElements = [];



    const renderCard = async () => {

      setIsLoading(true);

      setError("");



      try {

        const nonceResult = await stripe.createEphemeralKeyNonce({

          issuingCard: cardId,

        });



        const headers = await getAuthHeaders();

        const ephemeralKeyResult = await fetch(

          `${process.env.REACT_APP_baseUrl}create-ephermale-keys`,

          {

            method: "POST",

            headers: { Accept: "application/json", ...headers },

            body: JSON.stringify({

              cardId,

              nonce: nonceResult.nonce,

            }),

          }

        );



        const ephemeralKeyResponse = await ephemeralKeyResult.json();



        if (!ephemeralKeyResponse.ephemeralKeySecret) {

          throw new Error(

            ephemeralKeyResponse.error || "Failed to load card details"

          );

        }



        if (cancelled) return;



        const elementOptions = {

          issuingCard: cardId,

          ephemeralKeySecret: ephemeralKeyResponse.ephemeralKeySecret,

          nonce: nonceResult.nonce,

          style: STRIPE_ELEMENT_STYLE,

        };



        await new Promise((resolve) => requestAnimationFrame(resolve));



        if (cancelled) return;



        MOUNT_IDS.forEach((id) => {

          const el = document.getElementById(id);

          if (el) el.innerHTML = "";

        });



        const number = elements.create("issuingCardNumberDisplay", elementOptions);

        const expiry = elements.create("issuingCardExpiryDisplay", elementOptions);

        const cvc = elements.create("issuingCardCvcDisplay", elementOptions);



        mountedElements.push(number, expiry, cvc);



        number.mount("#card-number");

        expiry.mount("#card-expiry");

        cvc.mount("#card-cvc");



        mountedCardIdRef.current = cardId;



        if (!cancelled) {

          setIsLoading(false);

        }

      } catch (err) {

        if (!cancelled) {

          setError(err.message || "Failed to load card details");

          setIsLoading(false);

        }

      }

    };



    renderCard();



    return () => {

      cancelled = true;

      mountedCardIdRef.current = null;

      mountedElements.forEach((el) => {

        try {

          el.unmount();

          el.destroy();

        } catch (_) {

          // ignore cleanup errors

        }

      });

    };

  }, [stripe, elements, cardId]);



  return (

    <div className="issuing-card-wrap" aria-busy={isLoading}>

      <div

        className={`issuing-card-visual${

          revealed ? " issuing-card-visual--revealed" : ""

        }${frozen ? " issuing-card-visual--frozen" : ""}`}

      >

        {frozen && (

          <div className="issuing-card-frozen-banner" role="status">

            Frozen — transactions declined

          </div>

        )}

        <div

          className={`issuing-card-loading issuing-card-loading--overlay${

            isLoading ? "" : " issuing-card-loading--hidden"

          }`}

          aria-hidden={!isLoading}

        >

          <Loader size={40} />

        </div>



        <div className="issuing-card-top">

          <div className="issuing-card-chip" aria-hidden="true" />

          <button

            type="button"

            className="issuing-card-toggle"

            onClick={() => setRevealed((prev) => !prev)}

            disabled={isLoading}

            aria-pressed={revealed}

            aria-label={revealed ? "Hide card details" : "Show card details"}

          >

            <FontAwesomeIcon icon={revealed ? faEyeSlash : faEye} />

            <span>{revealed ? "Hide" : "Show"}</span>

          </button>

        </div>



        <span className="issuing-card-brand">{BRAND_FULL}</span>



        <div className="issuing-card-sensitive" aria-hidden={!revealed}>

          <div className="issuing-card-field issuing-card-field--number">

            <span className="issuing-card-field__label">Card number</span>

            <div className="issuing-card-field__value" id="card-number" />

          </div>



          <div className="issuing-card-row">

            <div className="issuing-card-field">

              <span className="issuing-card-field__label">Expiry</span>

              <div className="issuing-card-field__value" id="card-expiry" />

            </div>

            <div className="issuing-card-field">

              <span className="issuing-card-field__label">CVC</span>

              <div className="issuing-card-field__value" id="card-cvc" />

            </div>

          </div>

        </div>



        {!revealed && !isLoading && (

          <div className="issuing-card-mask" aria-hidden="true">

            <span className="issuing-card-mask__text">•••• •••• •••• ••••</span>

          </div>

        )}

      </div>



      {error && (

        <p className="cards-error" role="alert">

          {error}

        </p>

      )}

    </div>

  );

}



export default CardDetailsElements;

